/**
 * Read Aloud Context
 *
 * Extends TextToSpeechContext with advanced reading features:
 * - Page Narration Mode: Read through all readable elements in order
 * - Click-to-Read Mode: Click any element to read it
 * - Visual highlighting of currently reading element
 * - Reading queue management
 *
 * WCAG 2.1 Compliance:
 * - 1.3.2 Meaningful Sequence - Content in correct reading order
 * - 2.1.1 Keyboard - Fully keyboard accessible
 * - 2.4.7 Focus Visible - Visual feedback during reading
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  useRef,
} from 'react'
import { useTextToSpeech } from './TextToSpeechContext'
import { usePageContent } from '@/hooks/usePageContent'
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts'
import {
  findReadableElements,
  findClosestReadable,
  getReadableMetadata,
  type ReadableElementMeta,
} from '@lina/accessibility/components/Readable'

export type ReadingMode = 'off' | 'page-narration' | 'click-to-read'

interface ReadAloudContextType {
  // Reading mode state
  readingMode: ReadingMode
  setReadingMode: (mode: ReadingMode) => void

  // Page narration state
  isReadingPage: boolean
  isPaused: boolean
  currentReadableIndex: number
  readableElements: ReadableElementMeta[]

  // Page narration controls
  startPageNarration: () => void
  pausePageNarration: () => void
  resumePageNarration: () => void
  stopPageNarration: () => void
  skipToNext: () => void
  skipToPrevious: () => void

  // Click-to-read mode
  enableClickToRead: () => void
  disableClickToRead: () => void

  // Currently highlighted element
  highlightedElement: HTMLElement | null
}

const ReadAloudContext = createContext<ReadAloudContextType | undefined>(undefined)

export function ReadAloudProvider({ children }: { children: ReactNode }) {
  const {
    speak,
    pause: pauseTTS,
    resume: resumeTTS,
    stop: stopTTS,
    isPlaying,
    isPaused,
  } = useTextToSpeech()

  const pageContent = usePageContent()

  const [readingMode, setReadingMode] = useState<ReadingMode>('off')
  const [isReadingPage, setIsReadingPage] = useState(false)
  const [currentReadableIndex, setCurrentReadableIndex] = useState(-1)
  const [readableElements, setReadableElements] = useState<ReadableElementMeta[]>([])
  const [highlightedElement, setHighlightedElement] = useState<HTMLElement | null>(null)

  const queueRef = useRef<ReadableElementMeta[]>([])
  const highlightClassRef = useRef<string>('reading-highlight')
  const previousIsPlayingRef = useRef<boolean>(false)

  // Collect readable elements from the page
  const collectReadableElements = useCallback(() => {
    const elements = findReadableElements()
    const metadata = elements.map(getReadableMetadata)
    setReadableElements(metadata)
    queueRef.current = metadata
    return metadata
  }, [])

  // Highlight the current element being read
  const highlightElement = useCallback(
    (element: HTMLElement | null) => {
      // Remove previous highlight
      if (highlightedElement) {
        highlightedElement.classList.remove(highlightClassRef.current)
        highlightedElement.removeAttribute('data-reading-now')
      }

      // Add new highlight
      if (element) {
        element.classList.add(highlightClassRef.current)
        element.setAttribute('data-reading-now', 'true')

        // Scroll element into view if needed
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }

      setHighlightedElement(element)
    },
    [highlightedElement],
  )

  // Read a specific readable element
  const readElement = useCallback(
    (meta: ReadableElementMeta, index: number) => {
      if (!meta.text || !meta.text.trim()) {
        console.log('[ReadAloud] Skipping element with no text')
        return
      }

      // Clean the text
      const cleanText = meta.text
        .replace(/\s+/g, ' ') // Normalize whitespace
        .trim()

      if (!cleanText || cleanText.length < 2) {
        console.log('[ReadAloud] Skipping element with insufficient text:', cleanText)
        return
      }

      console.log(
        `[ReadAloud] Reading element ${index + 1}/${queueRef.current.length}:`,
        cleanText.substring(0, 100) + '...',
      )

      // Highlight if enabled
      if (meta.highlightOnRead) {
        highlightElement(meta.element)
      }

      setCurrentReadableIndex(index)

      // Construct announcement text
      const announcement = meta.label ? `${meta.label}. ${cleanText}` : cleanText

      console.log('[ReadAloud] Announcement text:', announcement)

      // Important: speak() must be called, which will set isPlaying to true
      speak(announcement)
      console.log('[ReadAloud] Called speak() for element', index + 1)
    },
    [speak, highlightElement],
  )

  // Read the next element in the queue
  const readNext = useCallback(() => {
    const queue = queueRef.current
    const nextIndex = currentReadableIndex + 1

    console.log(
      `[ReadAloud] readNext called. Current: ${currentReadableIndex}, Next: ${nextIndex}, Queue length: ${queue.length}`,
    )

    if (nextIndex < queue.length) {
      console.log('[ReadAloud] Reading next element...')
      readElement(queue[nextIndex], nextIndex)
    } else {
      // Finished reading all elements
      console.log('[ReadAloud] Finished reading all elements')
      stopPageNarration()
    }
  }, [currentReadableIndex, readElement])

  // Extract readable content from main area, skipping UI chrome
  const extractMainContent = useCallback((): string => {
    // Skip elements marked with data-skip-read-aloud
    const skipSelector = '[data-skip-read-aloud="true"]'

    // Priority 1: Try to find main content area
    const mainContent = document.querySelector('main#main-content')

    if (mainContent) {
      // Clone the element to manipulate without affecting the page
      const clone = mainContent.cloneNode(true) as HTMLElement

      // Remove all elements we want to skip
      clone.querySelectorAll(skipSelector).forEach((el) => el.remove())

      // Also remove common UI chrome patterns
      ;[
        'nav',
        'header',
        'aside',
        '[role="navigation"]',
        '[role="banner"]',
        '[role="complementary"]',
        '.skip-read-aloud',
        '[aria-hidden="true"]',
        '[hidden]',
      ].forEach((selector) => {
        clone.querySelectorAll(selector).forEach((el) => el.remove())
      })

      // Extract visible text
      const text = extractVisibleText(clone)
      if (text && text.length > 0) {
        console.log('[ReadAloud] Extracted main content:', text.substring(0, 100) + '...')
        return text
      }
    }

    // Priority 2: Look for content containers
    const contentSelectors = [
      '[role="main"]',
      'article',
      '.content',
      '[class*="content"]',
      '[class*="page"]',
    ]

    for (const selector of contentSelectors) {
      const element = document.querySelector(selector)
      if (element) {
        const clone = element.cloneNode(true) as HTMLElement
        clone.querySelectorAll(skipSelector).forEach((el) => el.remove())
        const text = extractVisibleText(clone)
        if (text && text.length > 20) {
          // Minimum content threshold
          console.log('[ReadAloud] Found content via selector:', selector)
          return text
        }
      }
    }

    // Priority 3: Last resort - body but with aggressive filtering
    console.log('[ReadAloud] Using body fallback with aggressive filtering')
    const body = document.body.cloneNode(true) as HTMLElement

    // Remove all common UI chrome
    ;[
      'nav',
      'header',
      'aside',
      'footer',
      '[role="navigation"]',
      '[role="banner"]',
      '[role="complementary"]',
      skipSelector,
      '.skip-read-aloud',
      '[aria-hidden="true"]',
      '[hidden]',
      'button',
      'a', // Remove standalone buttons/links
    ].forEach((selector) => {
      body.querySelectorAll(selector).forEach((el) => el.remove())
    })

    return extractVisibleText(body)
  }, [])

  // Helper to extract only visible text from an element
  const extractVisibleText = (element: HTMLElement): string => {
    // Simple approach: get direct text content
    const clone = element.cloneNode(true) as HTMLElement

    // Remove skip-read-aloud children
    clone.querySelectorAll('[data-skip-read-aloud="true"]').forEach((el) => el.remove())

    // Remove hidden children
    clone.querySelectorAll('[hidden], [aria-hidden="true"]').forEach((el) => el.remove())

    // Get all text
    const text = clone.textContent || ''

    // Clean up whitespace
    return text.replace(/\s+/g, ' ').trim()
  }

  // Auto-detect semantic content blocks for reading
  const autoDetectContentBlocks = useCallback((): ReadableElementMeta[] => {
    const blocks: ReadableElementMeta[] = []

    // Find main content area - skip the app header completely
    const mainContent = document.querySelector('main') || document.querySelector('[role="main"]')

    if (!mainContent) {
      console.error('[ReadAloud] No main content area found')
      return blocks
    }

    console.log('[ReadAloud] Scanning main content for readable blocks in DOM order...')

    const processedElements = new Set<HTMLElement>()

    // Track counts for numbering repeated elements
    const elementCounts = {
      button: 0,
      link: 0,
      heading: 0,
      paragraph: 0,
      select: 0,
      textarea: 0,
      input: 0,
      label: 0,
    }

    // Helper to check if element should be skipped
    const shouldSkipElement = (element: HTMLElement): boolean => {
      if (processedElements.has(element)) return true
      if (element.closest('[data-skip-read-aloud="true"]')) return true
      if (element.closest('nav, header, footer, aside')) return true
      return false
    }

    // Helper to process a single element
    const processElement = (element: HTMLElement): void => {
      if (shouldSkipElement(element)) return

      const tagName = element.tagName.toLowerCase()
      let text = ''
      let label = ''

      // Handle headings
      if (tagName === 'h1' || tagName === 'h2' || tagName === 'h3') {
        // Skip headings inside interactive elements (they're part of the button/link text)
        if (element.closest('button, a[role="button"]')) return

        text = extractVisibleText(element)
        if (text && text.length > 2) {
          elementCounts.heading++
          if (tagName === 'h1') {
            label = 'Page Heading'
          } else if (tagName === 'h2') {
            label = 'Section Heading'
          } else {
            label = 'Subsection Heading'
          }
        }
      }
      // Handle paragraphs (skip those inside buttons)
      else if (tagName === 'p' || tagName === 'span') {
        // Skip paragraphs/spans inside interactive elements
        if (element.closest('button, a[role="button"]')) return

        // Check if this is a character counter (e.g., "145/2000")
        const textContent = element.textContent?.trim() || ''
        const counterMatch = textContent.match(/^(\d+)\/(\d+)$/)
        if (counterMatch) {
          const limit = counterMatch[2]
          text = `Character limit: ${limit}`
          label = ''
          // Add it and return early
          blocks.push({
            element,
            order: blocks.length,
            label,
            text,
            highlightOnRead: true,
          })
          processedElements.add(element)
          console.log(`[ReadAloud] ✓ Added character limit: "${text}"`)
          return
        }

        // Skip tiny helper text
        if (element.className.includes('text-xs') || element.className.includes('text-fluid-xs'))
          return

        text = extractVisibleText(element)
        if (text && text.length > 5) {
          elementCounts.paragraph++

          // Check if this is a page description (follows Page Heading and is relatively short)
          const previousBlock = blocks[blocks.length - 1]
          const isPageDescription = previousBlock?.label === 'Page Heading' && text.length < 200
          label = isPageDescription ? 'Page Description' : ''
        }
      }
      // Handle textareas and inputs
      else if (tagName === 'textarea' || tagName === 'input') {
        const inputElement = element as HTMLInputElement | HTMLTextAreaElement
        const currentValue = inputElement.value?.trim() || ''
        const placeholder = element.getAttribute('placeholder')
        const inputType = tagName === 'textarea' ? 'Text Box' : 'Input Field'

        // Only include if there's a value or placeholder
        if (currentValue || (placeholder && placeholder.length > 0)) {
          elementCounts[tagName as 'textarea' | 'input']++
          label = inputType

          // Prioritize actual value over placeholder
          if (currentValue) {
            // Just read the content - let TTS chunking handle long text
            text = `Content: ${currentValue}`
          } else {
            text = `Placeholder: ${placeholder}`
          }
        }
      }
      // Handle labels (form field labels)
      else if (tagName === 'label') {
        text = extractVisibleText(element)
        if (text && text.length > 2) {
          // Check if this label is associated with a form control
          const forAttr = element.getAttribute('for')
          const associatedControl = forAttr
            ? document.getElementById(forAttr)
            : element.querySelector('input, textarea, select')

          if (associatedControl) {
            elementCounts.label++
            label = 'Field Label'
          }
        }
      }
      // Handle buttons and links with button role
      else if (
        tagName === 'button' ||
        (tagName === 'a' && element.getAttribute('role') === 'button')
      ) {
        // Check if this is a Select trigger (Radix UI pattern)
        const isSelectTrigger =
          element.hasAttribute('data-radix-select-trigger') ||
          element.getAttribute('aria-haspopup') === 'listbox'

        if (isSelectTrigger) {
          // This is a dropdown selector - get the displayed text
          const currentValue = extractVisibleText(element)

          // Prefer aria-label if available, otherwise try to find associated label
          const ariaLabel = element.getAttribute('aria-label')
          let dropdownLabel = 'Dropdown Menu'

          if (ariaLabel) {
            // Use the aria-label directly
            dropdownLabel = ariaLabel
          } else {
            // Fallback: look for nearby label element
            const parentContainer = element.closest('.flex, .space-y-3, div')
            if (parentContainer) {
              const labelElement = parentContainer.querySelector('label')
              if (labelElement) {
                dropdownLabel = extractVisibleText(labelElement)
              }
            }
          }

          elementCounts.select++
          label = 'Dropdown'

          // Check if a selection has been made (not a placeholder like "Select...")
          const hasSelection =
            currentValue &&
            !currentValue.toLowerCase().includes('select') &&
            !currentValue.toLowerCase().includes('choose')

          // If aria-label already includes good context, use it; otherwise append current value
          if (ariaLabel && ariaLabel.toLowerCase().includes('dropdown')) {
            // aria-label already describes it as a dropdown
            if (hasSelection) {
              text = `${ariaLabel}. Currently selected: ${currentValue}`
            } else {
              text = `${ariaLabel}. No selection made`
            }
          } else {
            // Standard format with context
            if (hasSelection) {
              text = `${dropdownLabel}. Current selection: ${currentValue}`
            } else {
              text = `${dropdownLabel}. ${currentValue || 'No selection made'}`
            }
          }
        } else {
          // Regular button/link
          // Check if any children were already processed
          let hasProcessedChild = false
          element.querySelectorAll('*').forEach((child) => {
            if (processedElements.has(child as HTMLElement)) {
              hasProcessedChild = true
            }
          })
          if (hasProcessedChild) return

          // Prefer aria-label if available
          const ariaLabel = element.getAttribute('aria-label')
          text = ariaLabel || extractVisibleText(element)

          if (text && text.length > 3) {
            const isButton = tagName === 'button'

            if (isButton) {
              elementCounts.button++
            } else {
              elementCounts.link++
            }

            const count = isButton ? elementCounts.button : elementCounts.link
            const baseLabel = isButton ? 'Button' : 'Link'

            // Parse text to extract title and description
            // Pattern: "Title. Description"
            const parts = text.split(/\.\s+/)

            if (parts.length >= 2 && parts[0].length < 50) {
              // Looks like "Title. Description" format
              const title = parts[0]
              const description = parts.slice(1).join('. ')
              text = `Title: ${title}. Description: ${description}`
            }

            // Add number if there are multiple buttons/links
            label =
              elementCounts.button + elementCounts.link > 1 ? `${baseLabel} ${count}` : baseLabel
          }
        }
      }

      // Add to blocks if we got valid text
      // Skip very short text (likely icons, abbreviations, or UI chrome)
      if (text && text.trim().length > 3) {
        blocks.push({
          element,
          order: blocks.length,
          label,
          text,
          highlightOnRead: true,
        })
        processedElements.add(element)
        console.log(`[ReadAloud] ✓ Added ${label || 'text'}: "${text.substring(0, 50)}..."`)
      } else if (text && text.trim().length > 0) {
        console.log(`[ReadAloud] ✗ Skipped short text: "${text}"`)
      }
    }

    // Walk through all relevant elements in DOM order
    // Note: span.text-xs/text-fluid-xs targets character counters and similar metadata
    const allElements = mainContent.querySelectorAll(
      'h1, h2, h3, p, span.text-xs, span.text-fluid-xs, label, button, a[role="button"], textarea, input[type="text"], input:not([type])',
    )

    allElements.forEach((element) => {
      processElement(element as HTMLElement)
    })

    // Post-processing: Remove page heading if there's no description after it
    if (blocks.length > 0 && blocks[0].label === 'Page Heading') {
      // Check if the next block is a Page Description
      const hasDescription = blocks.length > 1 && blocks[1].label === 'Page Description'
      if (!hasDescription) {
        console.log('[ReadAloud] Removing lone Page Heading (no description follows)')
        blocks.shift() // Remove the first element
        // Re-index remaining blocks
        blocks.forEach((block, index) => {
          block.order = index
        })
      }
    }

    console.log(`[ReadAloud] Auto-detected ${blocks.length} content blocks in DOM order`)
    return blocks
  }, [])

  // Start page narration
  const startPageNarration = useCallback(() => {
    // Stop any existing playback first
    stopTTS()
    previousIsPlayingRef.current = false

    // First try manual Readable components
    let elements = collectReadableElements()

    // If no Readable components, auto-detect content blocks
    if (elements.length === 0) {
      console.log('[ReadAloud] No Readable components found, auto-detecting content')
      elements = autoDetectContentBlocks()
    }

    if (elements.length === 0) {
      console.error('[ReadAloud] No content found to read')
      return
    }

    console.log(`[ReadAloud] Starting narration with ${elements.length} elements`)
    queueRef.current = elements
    setReadableElements(elements)
    setReadingMode('page-narration')
    setIsReadingPage(true)
    setCurrentReadableIndex(-1)

    // Start reading from first element (with delay to ensure state is set)
    setTimeout(() => {
      console.log('[ReadAloud] Starting first element...')
      readElement(elements[0], 0)
    }, 200)
  }, [collectReadableElements, autoDetectContentBlocks, readElement, stopTTS])

  // Pause page narration
  const pausePageNarration = useCallback(() => {
    pauseTTS()
  }, [pauseTTS])

  // Resume page narration
  const resumePageNarration = useCallback(() => {
    resumeTTS()
  }, [resumeTTS])

  // Stop page narration
  const stopPageNarration = useCallback(() => {
    console.log('[ReadAloud] Stopping page narration')
    stopTTS()
    previousIsPlayingRef.current = false
    setReadingMode('off')
    setIsReadingPage(false)
    setCurrentReadableIndex(-1)
    highlightElement(null)
  }, [stopTTS, highlightElement])

  // Skip to next element
  const skipToNext = useCallback(() => {
    stopTTS()
    // Increased delay to ensure speech is fully stopped before next element
    setTimeout(() => {
      readNext()
    }, 150)
  }, [stopTTS, readNext])

  // Skip to previous element
  const skipToPrevious = useCallback(() => {
    const prevIndex = Math.max(0, currentReadableIndex - 1)
    if (prevIndex !== currentReadableIndex) {
      stopTTS()
      // Increased delay to ensure speech is fully stopped before previous element
      setTimeout(() => {
        const queue = queueRef.current
        readElement(queue[prevIndex], prevIndex)
      }, 150)
    }
  }, [currentReadableIndex, stopTTS, readElement])

  // Handle click-to-read mode
  const handleClickToRead = useCallback(
    (event: MouseEvent) => {
      if (readingMode !== 'click-to-read') return

      const target = event.target as HTMLElement

      // Skip if clicking on elements marked to skip
      if (target.closest('[data-skip-read-aloud="true"]')) {
        console.log('[ReadAloud] Skipping element marked with data-skip-read-aloud')
        return
      }

      // First, try to find a manual Readable component
      const readable = findClosestReadable(target)

      if (readable) {
        // Found a Readable component - always handle this
        event.preventDefault()
        event.stopPropagation()
        const meta = getReadableMetadata(readable)
        readElement(meta, -1)
        return
      }

      // Use the same auto-detected elements that page narration uses
      const queue = queueRef.current

      if (queue.length === 0) {
        console.log('[ReadAloud] No readable elements found, allowing click through')
        return
      }

      // Find the closest readable element from the auto-detected queue
      let closestElement: ReadableElementMeta | null = null
      let closestDistance = Infinity

      for (const meta of queue) {
        // Check if the clicked target is inside this readable element
        if (meta.element.contains(target)) {
          closestElement = meta
          closestDistance = 0
          break
        }

        // Calculate distance to this element
        const rect1 = target.getBoundingClientRect()
        const rect2 = meta.element.getBoundingClientRect()

        const distance = Math.sqrt(
          Math.pow(rect1.left - rect2.left, 2) + Math.pow(rect1.top - rect2.top, 2),
        )

        if (distance < closestDistance) {
          closestDistance = distance
          closestElement = meta
        }
      }

      if (closestElement && closestElement.text) {
        // Found a readable element to read
        event.preventDefault()
        event.stopPropagation()

        console.log(
          '[ReadAloud] Click-to-read (auto-detected):',
          closestElement.text.substring(0, 50) + '...',
        )

        // Read the element using the same method as page narration
        readElement(closestElement, -1)

        // Highlight temporarily
        setTimeout(() => {
          if (highlightedElement === closestElement?.element) {
            highlightElement(null)
          }
        }, 3000)
      } else {
        // No readable content found, allow the click through
        console.log('[ReadAloud] No readable content found, allowing click through')
      }
    },
    [readingMode, readElement, highlightElement, highlightedElement],
  )

  // Enable click-to-read mode
  const enableClickToRead = useCallback(() => {
    console.log('[ReadAloud] Enabling click-to-read mode')

    // Collect readable elements using the same logic as page narration
    let elements = collectReadableElements()

    // If no Readable components, auto-detect content blocks
    if (elements.length === 0) {
      console.log(
        '[ReadAloud] No Readable components found, auto-detecting content for click-to-read',
      )
      elements = autoDetectContentBlocks()
    }

    if (elements.length > 0) {
      console.log(`[ReadAloud] Click-to-read ready with ${elements.length} elements`)
      queueRef.current = elements
      setReadableElements(elements)
    } else {
      console.warn('[ReadAloud] No readable elements found for click-to-read mode')
    }

    setReadingMode('click-to-read')
  }, [collectReadableElements, autoDetectContentBlocks])

  // Disable click-to-read mode
  const disableClickToRead = useCallback(() => {
    console.log('[ReadAloud] Disabling click-to-read mode')
    setReadingMode('off')
    stopTTS()
    highlightElement(null)
  }, [stopTTS, highlightElement])

  // Monitor TTS completion to advance to next element
  useEffect(() => {
    const wasPlaying = previousIsPlayingRef.current
    previousIsPlayingRef.current = isPlaying

    console.log('[ReadAloud] TTS state:', {
      wasPlaying,
      isPlaying,
      isPaused,
      readingMode,
      currentReadableIndex,
    })

    // Only advance when we transition from playing to not-playing
    // This prevents triggering before speech starts
    if (
      readingMode === 'page-narration' &&
      isReadingPage &&
      wasPlaying && // Was playing before
      !isPlaying && // Now not playing
      !isPaused && // And not paused
      currentReadableIndex >= 0
    ) {
      console.log('[ReadAloud] Speech finished, advancing to next element')
      readNext()
    }
  }, [isPlaying, isPaused, readingMode, isReadingPage, currentReadableIndex, readNext])

  // Set up click-to-read listener
  useEffect(() => {
    if (readingMode === 'click-to-read') {
      document.addEventListener('click', handleClickToRead, true)

      // Add visual indicator to readable elements
      document.body.classList.add('click-to-read-mode')

      return () => {
        document.removeEventListener('click', handleClickToRead, true)
        document.body.classList.remove('click-to-read-mode')
      }
    }
  }, [readingMode, handleClickToRead])

  // Keyboard shortcuts for TTS controls
  useKeyboardShortcuts(
    [
      {
        key: 'F9',
        callback: () => {
          if (isReadingPage) {
            if (isPaused) {
              resumePageNarration()
            } else {
              pausePageNarration()
            }
          } else {
            startPageNarration()
          }
        },
        preventDefault: true,
        description: 'Toggle TTS play/pause (F9)',
      },
      {
        key: ' ', // Space key
        callback: () => {
          if (isReadingPage) {
            if (isPaused) {
              resumePageNarration()
            } else {
              pausePageNarration()
            }
          } else {
            startPageNarration()
          }
        },
        preventDefault: true,
        description: 'Toggle TTS play/pause (Space)',
      },
      {
        key: 'F8',
        callback: () => {
          if (isReadingPage && readableElements.length > 1) {
            skipToNext()
          }
        },
        preventDefault: true,
        description: 'Skip to next section (F8)',
      },
      {
        key: 'F7',
        callback: () => {
          if (isReadingPage && readableElements.length > 1) {
            skipToPrevious()
          }
        },
        preventDefault: true,
        description: 'Skip to previous section (F7)',
      },
      {
        key: 'Escape',
        callback: () => {
          if (isReadingPage) {
            stopPageNarration()
          }
        },
        preventDefault: false, // Don't prevent default for Escape (let it close modals too)
        description: 'Stop reading (Escape)',
      },
    ],
    true,
  ) // Always enabled

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTTS()
      highlightElement(null)
    }
  }, [])

  const value: ReadAloudContextType = {
    readingMode,
    setReadingMode,
    isReadingPage,
    isPaused,
    currentReadableIndex,
    readableElements,
    startPageNarration,
    pausePageNarration,
    resumePageNarration,
    stopPageNarration,
    skipToNext,
    skipToPrevious,
    enableClickToRead,
    disableClickToRead,
    highlightedElement,
  }

  return <ReadAloudContext.Provider value={value}>{children}</ReadAloudContext.Provider>
}

export function useReadAloud() {
  const context = useContext(ReadAloudContext)
  if (context === undefined) {
    throw new Error('useReadAloud must be used within a ReadAloudProvider')
  }
  return context
}
